import {Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {AdminValetService} from '../../../../../admin/modules/admin-valet/services/admin-valet.service';
import {TranslateService} from '@ngx-translate/core';
import {combineLatest, Observable, Subject} from 'rxjs';
import {skip, take, takeUntil, takeWhile} from 'rxjs/operators';
import {PvModalDialogService} from '../../../../../shared/services/pv-modal-dialog.service';
import { GridColumnConfig } from 'src/app/shared/models/grid-config.interface';
import { ColumnSettingsItem } from 'src/app/shared/components/shared-list/column-settings/column-settings.component';
import { ButtonsConfig } from 'src/app/shared/models/button.interface';
import { FilterConfig, FilterData } from 'src/app/shared/models/filter-config.interface';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonValetWithholdingConfig } from '../../../config/common-valet-withholding.config';
import { GenericDatasource } from 'src/app/shared/datasource/generic.datasource';
import { LinkDisplayColumnComponent } from 'src/app/shared/components/link-display-column/link-display-column.component';

@Component({
  selector: 'pv-common-valet-withholding-grid',
  templateUrl: './common-valet-withholding-grid.component.html',
  styleUrls: ['./common-valet-withholding-grid.component.scss']
})
export class CommonValetWithholdingGridComponent extends GenericDatasource<any> implements OnInit, OnDestroy {

  private readonly FILTERS_STORAGE_KEY = 'commonValetWithHoldingFilters';
  private readonly COLUMN_SETTINGS_STORAGE_KEY = 'commonValetWithHoldingColumnsSettings';
  private readonly COLUMNS_SORT_STORAGE_KEY = 'commonValetWithHoldingSort';

  @Input() valetId: string;
  @Input() invoicingUnblocked: Observable<void>;

  public isDataSourceLoading = true;
  public columns: GridColumnConfig[] = [];
  public columnSettings: ColumnSettingsItem[] = [];
  public showColumnSettings = true;
  
  private sub: any;
  private destroy$: Subject<void> = new Subject<void>();

  public buttonsConfig: ButtonsConfig = {
    exportAll: { show: false, isLoading: false },
    export: { show: true, isLoading: false },
    refresh: { show: true, isLoading: false }
  };
  
  public get isLoading(): boolean {
    return this.isDataSourceLoading;
  }

  public filtersConfig: FilterConfig[] = [];
  public showClearFiltersButton: boolean = false;
  public initialFilterValues: FilterData = {};

  public availableRemunerationAmount: number | null = null;
  public availableExpenseAmount: number | null = null;
  public availableTotalAmount: number | null = null;

  @ViewChild('totalAmountTemplate', { static: true }) totalAmountTemplate: TemplateRef<LinkDisplayColumnComponent>;
  @ViewChild('remainingAmountTemplate', { static: true }) remainingAmountTemplate: TemplateRef<LinkDisplayColumnComponent>;
  @ViewChild('allowInExpensesTemplate', { static: true }) allowInExpensesTemplate: TemplateRef<LinkDisplayColumnComponent>;
  @ViewChild('actionsTemplate', { static: true }) actionsTemplate: TemplateRef<LinkDisplayColumnComponent>;
  
  constructor(private adminValetService: AdminValetService,
              private translateService: TranslateService,
              private commonValetWithHoldingConfig: CommonValetWithholdingConfig,
              private activeRoute: ActivatedRoute,
              private router: Router,
              private pvModalService: PvModalDialogService) {
    super();
  }

  ngOnInit(): void {
    const config = this.commonValetWithHoldingConfig.createGridConfig(
      this.FILTERS_STORAGE_KEY,
      this.COLUMNS_SORT_STORAGE_KEY,
      this.adminValetService,
      this.router,
      this.activeRoute,
      this.valetId
    );
    this.setFilter('valetId', this.valetId);
    this.initialize(config);

    this.updateWithholding = this.updateWithholding.bind(this);
    this.deleteWithholding = this.deleteWithholding.bind(this);

    this.initializeColumns();
    this.setupColumnSettings();
    this.handleRouteParams();
    this.setupDataSource();

    this.adminValetService.getAmountAvailableToWithhold(this.valetId).then(res => {
      this.availableRemunerationAmount = res.remuneration;
      this.availableExpenseAmount = res.expense;
      this.availableTotalAmount = res.remuneration + res.expense;
    });
    this.invoicingUnblocked.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.adminValetService.hasActiveWithholdings(this.valetId).then(hasActiveWithholdings => {
        if (!hasActiveWithholdings) {
          this.pvModalService.openValidationModal({
            title: this.translateService.instant('modules.valet.valet_profile.withholdings.invoicing_unblocked_modal.title'),
            message: this.translateService.instant('modules.valet.valet_profile.withholdings.invoicing_unblocked_modal.message'),
            showCancel: false
          }).pipe(
            takeWhile( (v) => !v.canceled && !v.backdropClicked),
            take(1)
          ).subscribe(() => {});
        }
      });
    });
  }

  private handleRouteParams() {
    this.activeRoute.queryParams
      .pipe(takeUntil(this.destroy$), skip(1))
      .subscribe(() => {});
  }

  get valetId$() { 
    return this.getFilter('valetId'); 
  }

  private initializeColumns() {
    this.columns = this.setColumns();
  }

  public setColumns() {
    return this.commonValetWithHoldingConfig.getCommonValetWithholdingColumnConfig(
      this.totalAmountTemplate,
      this.remainingAmountTemplate,
      this.allowInExpensesTemplate,
      this.actionsTemplate);
  }

  private setupColumnSettings() {
    this.columnSettings = this.initializeColumnSettings(
      this.columns,
      this.COLUMN_SETTINGS_STORAGE_KEY,
      ['name']
    );
    this.applyColumnVisibility();
  }

  public onColumnVisibilityChanged(columnSettings: ColumnSettingsItem[]) {
    this.columnSettings = columnSettings;
    localStorage.setItem(this.COLUMN_SETTINGS_STORAGE_KEY, JSON.stringify(columnSettings));
    this.applyColumnVisibility();
  }

  private applyColumnVisibility() {
    const allColumns = this.setColumns();
    this.columns = allColumns.filter(column => {
      const setting = this.columnSettings.find(s => s.key === column.key);
      return setting ? setting.visible : true;
    });
  }

  private setupDataSource() {
    combineLatest([
      this.connect(null),
      this.loading$
    ])
    .pipe(takeUntil(this.destroy$))
    .subscribe(([data, isLoading]) => {
      this.isDataSourceLoading = isLoading;
    });
  }

  public formatAmount(amount: any): string {
    return this.adminValetService.formatAmount(amount);
  }

  public formatAllowInExpenses(row: any): string {
    return this.adminValetService.formatAllowInExpenses(row);
  }

  public onClearAllFilters(): void {
    this.clearAllFilters();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  public createWithholding() {
    this.adminValetService.openWithholdingDialog(null).pipe(takeUntil(this.destroy$)).subscribe(res => {
      if (res.data) {
        this.adminValetService.createWithholding(this.valetId, res.data).then(() => {
          res.dialog.close();
          this.refresh();
        });
      } else {
        res.dialog.close();
      }
    });
  }

  public updateWithholding(row: any) {
    this.adminValetService.openWithholdingDialog(row).pipe(takeUntil(this.destroy$)).subscribe(res => {
      if (res.data) {
        this.adminValetService.updateWithholding(this.valetId, row.id, res.data).then(() => {
          res.dialog.close();
          this.refresh();
        });
      } else {
        res.dialog.close();
      }
    });
  }

  public deleteWithholding(row: any) {
    this.pvModalService.openValidationModal({
      message: this.translateService.instant('modules.valet.valet_profile.withholdings.grid.delete', {
        name: row.name
      })
    }).pipe(
      takeWhile( (v) => !v.canceled && !v.backdropClicked),
      take(1)
    ).subscribe(res => {
      if (res.confirmed) {
        this.adminValetService.deleteWithholding(this.valetId, row.id).then(() => {
          this.refresh();
        });
      }
    });
  }

  public onExportPageData(): void {
    this.exportCurrentPageData(this.buttonsConfig);
  }
}
